import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { format, setHours, setMinutes, addDays, subDays, isEqual, startOfMinute } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CalendarEvent } from "@/types/calendar-view";
import { CalendarDayColumn } from "./CalendarDayColumn";
import { CalendarEventBlock } from "./CalendarEventBlock";

// ---------------------------------------------------------------------------
// CalendarDayView — single-day time grid for the modal sidebar
// ---------------------------------------------------------------------------

interface CalendarDayViewProps {
  date: Date;
  events: CalendarEvent[];
  workingHours?: { start: number; end: number };
  slotDuration?: number;
  slotHeight?: number;
  selectedSlot?: Date;
  className?: string;
  onSlotClick?: (startTime: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onDateChange?: (newDate: Date) => void;
  onEventMove?: (eventId: string, newStartTime: Date) => void;
  onEventResize?: (eventId: string, newDurationMin: number) => void;
}

export const CalendarDayView = ({
  date,
  events,
  workingHours = { start: 7, end: 19 },
  slotDuration = 30,
  slotHeight = 40,
  selectedSlot,
  className,
  onSlotClick,
  onEventClick,
  onDateChange,
  onEventMove,
  onEventResize,
}: CalendarDayViewProps) => {
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    })
  );

  // Time labels
  const timeLabels = useMemo(() => {
    const labels: { hour: number; minute: number; label: string }[] = [];
    for (let h = workingHours.start; h < workingHours.end; h++) {
      for (let m = 0; m < 60; m += slotDuration) {
        labels.push({
          hour: h,
          minute: m,
          label: `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`,
        });
      }
    }
    return labels;
  }, [workingHours, slotDuration]);

  // Auto-scroll to current hour
  useEffect(() => {
    if (scrollRef.current) {
      const now = new Date();
      const minutesSinceStart =
        (now.getHours() - workingHours.start) * 60 + now.getMinutes();
      const pxPerMinute = slotHeight / slotDuration;
      const scrollTo = Math.max(0, minutesSinceStart * pxPerMinute - 80);
      scrollRef.current.scrollTop = scrollTo;
    }
  }, [date]);

  // DnD
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const ev = (event.active.data.current as any)?.event as CalendarEvent | undefined;
    setDraggedEvent(ev ?? null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggedEvent(null);
      const { active, over } = event;
      if (!over || !active || !onEventMove) return;

      const slotTime = (over.data.current as any)?.slotTime as Date | undefined;
      if (!slotTime) return;

      const combined = setMinutes(
        setHours(new Date(date), slotTime.getHours()),
        slotTime.getMinutes()
      );

      const calEvent = (active.data.current as { event?: CalendarEvent })?.event;
      if (calEvent) {
        const newStart = startOfMinute(combined);
        const prevStart = startOfMinute(calEvent.start);
        if (isEqual(prevStart, newStart)) {
          return;
        }
      }

      onEventMove(active.id as string, combined);
    },
    [date, onEventMove]
  );

  const handleSlotClick = useCallback(
    (startTime: Date) => {
      const combined = setMinutes(
        setHours(new Date(date), startTime.getHours()),
        startTime.getMinutes()
      );
      onSlotClick?.(combined);
    },
    [date, onSlotClick]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className={cn("flex flex-col h-full overflow-hidden", className)}>
        {/* Day navigation header */}
        <div className="flex items-center justify-between px-2 py-2 border-b border-border">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => onDateChange?.(subDays(date, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium text-foreground">
            {format(date, "EEE, d MMM", { locale: ptBR })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => onDateChange?.(addDays(date, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Scrollable time grid */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto sidebar-style-scroll">
          <div className="flex min-h-0">
            {/* Time gutter */}
            <div className="w-10 flex-shrink-0">
              {timeLabels.map((t) => {
                const isHour = t.minute === 0;
                return (
                  <div
                    key={`${t.hour}-${t.minute}`}
                    className="flex items-start justify-end pr-1.5 text-muted-foreground"
                    style={{ height: `${slotHeight}px` }}
                  >
                    <span
                      className={cn(
                        isHour
                          ? "text-[10px] font-medium -mt-1.5"
                          : "text-[8px] opacity-50 -mt-1"
                      )}
                    >
                      {isHour ? t.label : ""}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Day column */}
            <CalendarDayColumn
              date={date}
              events={events}
              startHour={workingHours.start}
              endHour={workingHours.end}
              slotDuration={slotDuration}
              slotHeight={slotHeight}
              compact
              selectedSlot={selectedSlot}
              onSlotClick={handleSlotClick}
              onEventClick={onEventClick}
              onResize={onEventResize}
            />
          </div>
        </div>
      </div>

      {/* Drag overlay */}
      <DragOverlay style={{ zIndex: 1000 }}>
        {draggedEvent ? (
          <div className="w-[140px] opacity-80">
            <CalendarEventBlock
              event={draggedEvent}
              top={0}
              height={40}
              compact
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
